/**
 * Shared stated-credit, application-military, and primary-contact steps.
 *
 * These are the Wave 5 steps, lifted here unchanged by WI-TV-W6-003 exactly as
 * WI-TV-W6-001 lifted the field primitives into `step-fields.ts`. The purchase
 * and refinance journeys ask these questions identically, so they share one
 * implementation: the same value keys, the same authoritative enums, the same
 * validation, the same Skip semantics, and the same accessibility contract.
 * Nothing here is purpose-aware, which is why the locked military truth table
 * and credit mapping cannot drift between the two journeys.
 *
 * The military question is application-level: it is asked once, and there is no
 * borrower-level, co-borrower, or service-status answer anywhere in this module.
 */
import {
  CREDIT_SCORE_RANGE_OPTIONS,
  MILITARY_COMPONENT_OPTIONS,
  type SelectOption,
} from "../models/referral";
import {
  PHONE_DISPLAY_MASK,
  bindPhoneInput,
  bindRadioGroup,
  bindValueInputs,
  escapeHtml,
  formatPhoneDisplay,
  radioGroupHtml,
  textFieldHtml,
} from "./step-fields";
import type { WizardFieldError, WizardStep, WizardValues } from "./wizard-machine";

/** Value keys captured by these steps (authoritative model names). */
export const CREDIT_SCORE_RANGE_KEY = "creditScoreRange";
export const MILITARY_ANSWER_KEY = "militaryAnswer";
export const MILITARY_COMPONENT_KEY = "militaryComponent";
export const FIRST_NAME_KEY = "firstName";
export const LAST_NAME_KEY = "lastName";
export const PHONE_KEY = "phone";
export const EMAIL_KEY = "email";

/**
 * The exact application-level military question. It is asked once for the
 * application; there is no borrower-level or co-borrower answer.
 */
export const MILITARY_PROMPT =
  "Do you or your co-borrower have military service that may qualify you for a VA home loan?";

/** The three answers to {@link MILITARY_PROMPT}; skipping leaves it unanswered. */
export const MILITARY_ANSWER_OPTIONS: readonly SelectOption[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "notSure", label: "Not sure" },
];

/** Characters a borrower may use to format a phone number. */
const PHONE_FORMATTING_REGEX = /^[\d\s()+.-]+$/;

/** Deliberately permissive but structural: local part, `@`, dotted domain. */
const EMAIL_REGEX = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

/** Strips formatting characters, leaving only the digits a borrower typed. */
export function phoneDigits(value: string): string {
  return value.replace(/\D/g, "");
}

/** True when the text is a phone number of exactly ten digits once formatted characters are removed. */
export function isValidPhoneInput(value: string): boolean {
  const trimmed = value.trim();
  return PHONE_FORMATTING_REGEX.test(trimmed) && phoneDigits(trimmed).length === 10;
}

/** True when the text is structurally a single email address. */
export function isValidEmailInput(value: string): boolean {
  return EMAIL_REGEX.test(value.trim());
}

/**
 * Stated credit. Optional with an explicit Skip: the borrower picks one of the
 * exact authoritative credit ranges or moves on. A skipped/unanswered step emits
 * no credit value at all, so nothing is invented on the borrower's behalf.
 */
export const creditStep: WizardStep = {
  id: "credit",
  title: "Stated credit",
  optional: true,
  validate: (): WizardFieldError[] => [],
  render: (container: HTMLElement, values: WizardValues): void => {
    container.innerHTML = `
      <p class="wizard-step-desc" id="credit-desc">
        Roughly where does your credit score fall? This is optional — you can skip it.
      </p>
      ${radioGroupHtml(
        CREDIT_SCORE_RANGE_KEY,
        "Stated credit score range",
        CREDIT_SCORE_RANGE_OPTIONS,
        values.get(CREDIT_SCORE_RANGE_KEY),
        { autofocus: true, describedBy: "credit-desc" },
      )}`;

    bindRadioGroup(container, CREDIT_SCORE_RANGE_KEY, values);
  },
};

/**
 * True when a seed carries a military answer that supersedes an earlier one and
 * asserts no service, so any component held alongside it is now stale.
 *
 * The prefill sanitizers already refuse to seed a component without an
 * affirmative answer, but they cannot see the answers a seed lands on top of.
 * Whoever holds that state — the mounted wizard or the selector's pending seed —
 * applies this rule so the step's own stale-clearing behaviour holds on the
 * prefill path too: a component the borrower can no longer see is dropped, and a
 * later answer-only affirmative can never resurrect or map it without a fresh
 * component answer.
 */
export function supersedesMilitaryComponent(seeded: Readonly<Record<string, string>>): boolean {
  const answer = seeded[MILITARY_ANSWER_KEY];
  return answer !== undefined && answer !== "yes";
}

/**
 * Military eligibility. One optional, application-level question with a
 * same-step expansion: answering Yes reveals a single component selector.
 *
 * The answer and the component are independent answers, never coalesced: an
 * unanswered or "Not sure" application asserts nothing, "No" asserts a negative
 * answer, and "Yes" asserts a positive one with the component only when a
 * component with an exact LMD equivalent was picked. Changing away from Yes
 * clears the component immediately so a stale expansion answer can never be
 * submitted.
 */
/** The control the branch requirement points its error at, for focus/announce. */
const MILITARY_COMPONENT_FIELD = `${MILITARY_COMPONENT_KEY}-${MILITARY_COMPONENT_OPTIONS[0].value.replace(
  /[^A-Za-z0-9]+/g,
  "-",
)}`;

/** True when a component with an exact LMD equivalent is currently selected. */
function hasValidMilitaryComponent(values: WizardValues): boolean {
  return MILITARY_COMPONENT_OPTIONS.some((o) => o.value === values.get(MILITARY_COMPONENT_KEY));
}

export const militaryStep: WizardStep = {
  id: "military",
  title: "Military eligibility",
  optional: true,
  // The question itself is optional and skippable, but once Yes reveals the
  // component group a component becomes required: Continue stays blocked until a
  // valid branch is chosen. No/Not sure/Skip clear the group and assert nothing.
  validate: (values: WizardValues): WizardFieldError[] => {
    if (values.get(MILITARY_ANSWER_KEY) !== "yes") return [];
    if (hasValidMilitaryComponent(values)) return [];
    return [
      {
        field: MILITARY_COMPONENT_FIELD,
        message: "Select your military component to continue.",
      },
    ];
  },
  // An explicit Skip abandons the question itself, so both the answer and any
  // expansion component are cleared: a skipped application asserts neither
  // isMilitary nor militaryBranch.
  onSkip: (values: WizardValues): void => {
    values.set(MILITARY_ANSWER_KEY, "");
    values.set(MILITARY_COMPONENT_KEY, "");
  },
  render: (container: HTMLElement, values: WizardValues): void => {
    const renderExpansion = (): void => {
      const slot = container.querySelector<HTMLElement>("[data-military-expansion]");
      if (!slot) return;
      if (values.get(MILITARY_ANSWER_KEY) !== "yes") {
        slot.innerHTML = "";
        return;
      }
      slot.innerHTML = `
        <div class="wizard-conditional-group" role="group" aria-labelledby="military-component-desc">
          <p class="wizard-step-desc" id="military-component-desc">
            Which component did you or your co-borrower serve in?
            <span class="wizard-req" aria-hidden="true">*</span>
          </p>
          <p class="wizard-hint" id="military-component-hint">Required to continue.</p>
          ${radioGroupHtml(
            MILITARY_COMPONENT_KEY,
            "Military component",
            MILITARY_COMPONENT_OPTIONS,
            values.get(MILITARY_COMPONENT_KEY),
            { describedBy: "military-component-desc military-component-hint" },
          )}
        </div>`;
      bindRadioGroup(slot, MILITARY_COMPONENT_KEY, values);
    };

    container.innerHTML = `
      <p class="wizard-step-desc" id="military-desc">${escapeHtml(MILITARY_PROMPT)}</p>
      ${radioGroupHtml(
        MILITARY_ANSWER_KEY,
        MILITARY_PROMPT,
        MILITARY_ANSWER_OPTIONS,
        values.get(MILITARY_ANSWER_KEY),
        { autofocus: true, labelledBy: "military-desc" },
      )}
      <div data-military-expansion></div>`;

    bindRadioGroup(container, MILITARY_ANSWER_KEY, values, (answer) => {
      if (answer !== "yes") values.set(MILITARY_COMPONENT_KEY, "");
      renderExpansion();
    });
    renderExpansion();
  },
};

/** Primary borrower name. Both parts are required. */
export const borrowerNameStep: WizardStep = {
  id: "borrower-name",
  title: "Your name",
  validate: (values: WizardValues): WizardFieldError[] => {
    const errors: WizardFieldError[] = [];
    if (values.get(FIRST_NAME_KEY).trim() === "") {
      errors.push({ field: FIRST_NAME_KEY, message: "Enter your first name to continue." });
    }
    if (values.get(LAST_NAME_KEY).trim() === "") {
      errors.push({ field: LAST_NAME_KEY, message: "Enter your last name to continue." });
    }
    return errors;
  },
  render: (container: HTMLElement, values: WizardValues): void => {
    container.innerHTML = `
      <p class="wizard-step-desc">The primary borrower's legal name.</p>
      ${textFieldHtml(FIRST_NAME_KEY, "First name", values.get(FIRST_NAME_KEY), {
        required: true,
        autocomplete: "given-name",
      })}
      ${textFieldHtml(LAST_NAME_KEY, "Last name", values.get(LAST_NAME_KEY), {
        required: true,
        autocomplete: "family-name",
      })}`;

    bindValueInputs(container, values);
  },
};

/**
 * Phone number. Any familiar formatting is accepted while typing, but the answer
 * must contain exactly ten digits once formatting characters are removed, which
 * is what the canonical payload carries.
 */
export const borrowerPhoneStep: WizardStep = {
  id: "borrower-phone",
  title: "Your phone number",
  validate: (values: WizardValues): WizardFieldError[] => {
    const raw = values.get(PHONE_KEY);
    if (raw.trim() === "") {
      return [{ field: PHONE_KEY, message: "Enter your phone number to continue." }];
    }
    if (!isValidPhoneInput(raw)) {
      return [
        {
          field: PHONE_KEY,
          message: "Enter a 10-digit US phone number, for example (512) 555-0134.",
        },
      ];
    }
    return [];
  },
  render: (container: HTMLElement, values: WizardValues): void => {
    container.innerHTML = `
      <p class="wizard-step-desc">A phone number where we can reach you.</p>
      ${textFieldHtml(PHONE_KEY, "Phone number", formatPhoneDisplay(values.get(PHONE_KEY)), {
        required: true,
        type: "tel",
        inputmode: "tel",
        autocomplete: "tel",
        placeholder: PHONE_DISPLAY_MASK,
        describedBy: "phone-hint",
      })}
      <p class="wizard-hint" id="phone-hint">Format: ${PHONE_DISPLAY_MASK}</p>`;

    bindPhoneInput(container, PHONE_KEY, values);
  },
};

/**
 * Builds the email step. Only the follow-up sentence differs between journeys;
 * the required/valid-address contract and the control itself are identical.
 */
export function createBorrowerEmailStep(description: string): WizardStep {
  return {
    id: "borrower-email",
    title: "Your email",
    validate: (values: WizardValues): WizardFieldError[] => {
      const raw = values.get(EMAIL_KEY);
      if (raw.trim() === "") {
        return [{ field: EMAIL_KEY, message: "Enter your email address to continue." }];
      }
      if (!isValidEmailInput(raw)) {
        return [
          {
            field: EMAIL_KEY,
            message: "Enter a valid email address, for example name@example.com.",
          },
        ];
      }
      return [];
    },
    render: (container: HTMLElement, values: WizardValues): void => {
      container.innerHTML = `
        <p class="wizard-step-desc">${escapeHtml(description)}</p>
        ${textFieldHtml(EMAIL_KEY, "Email address", values.get(EMAIL_KEY), {
          required: true,
          type: "email",
          inputmode: "email",
          autocomplete: "email",
        })}`;

      bindValueInputs(container, values);
    },
  };
}
